"use client";

import React from "react";

export interface GqWebSwatch {
    name: string;
    hex: string;
    description: string;
}

export interface GqWebTypographyColorsBlockProps {
    tag?: string;
    title?: string;
    description?: string;
    sublabel?: string;
    fontName?: string;
    fontPreview?: string;
    fontDesc?: string;
    swatches?: GqWebSwatch[];
}

export default function GqWebTypographyColorsBlock({
    tag = "03 / DESIGN SYSTEM",
    title = "Typography & Colors",
    description = "We crafted a warm, editorial-grade visual system that conveys hospitality trust and brand warmth. The typography balances readability with elegance, while the color palette draws from natural textures.",
    sublabel = "VISUAL IDENTITY SYSTEM",
    fontName = "Inter + System Sans-Serif Stack",
    fontPreview = "AaBbCc123",
    fontDesc = "Used across all heading scales, body copy, and UI elements. The clean geometric structure preserves readability on mobile while retaining the premium editorial feel hosts expect.",
    swatches = [
        {
            name: "Warm Linen White",
            hex: "#FDFBF8",
            description: "The primary background canvas. A warm off-white that softens screen glare and conveys editorial calm."
        },
        {
            name: "Copper Amber",
            hex: "#D4A574",
            description: "The signature brand accent. Applied to CTAs, tags, highlights, and interactive states across the marketing site."
        },
        {
            name: "Charcoal Ink",
            hex: "#1A1A1A",
            description: "Primary text color. A rich near-black that maintains sharpness and editorial weight across all body copy."
        }
    ]
}: GqWebTypographyColorsBlockProps) {
    return (
        <section className="py-24 px-6 md:px-12 lg:px-24 bg-[#FAF8F5] border-b border-[#EDE6DD] text-[#444] font-sans antialiased w-full">
            <div className="container mx-auto">
                <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-20">
                    
                    {/* Sticky left panel */}
                    <div className="lg:col-span-4 flex flex-col justify-between">
                        <div>
                            {tag && (
                                <span className="text-xs font-mono text-[#D4A574] tracking-widest block mb-2 uppercase font-semibold">
                                    {tag}
                                </span>
                            )}
                            {title && (
                                <h2 className="text-4xl font-light text-[#1A1A1A] leading-tight">
                                    {title}
                                </h2>
                            )}
                            {description && (
                                <p className="text-zinc-500 text-xs mt-4 font-light leading-relaxed max-w-sm">
                                    {description}
                                </p>
                            )}
                        </div>
                        {sublabel && (
                            <div className="hidden lg:block font-mono text-[10px] text-zinc-400">
                                {sublabel}
                            </div>
                        )}
                    </div>

                    {/* Right Content */}
                    <div className="lg:col-span-8 space-y-12">
                        
                        {/* Font Stack Box */}
                        <div className="border border-[#EDE6DD] bg-white rounded-2xl p-8 relative overflow-hidden shadow-sm">
                            <span className="text-[9px] font-mono text-zinc-450 uppercase block mb-6 font-semibold">TYPOGRAPHY STACK</span>
                            
                            <div className="space-y-4">
                                {fontName && (
                                    <span className="text-[10px] font-mono text-[#D4A574] uppercase block font-semibold">
                                        {fontName}
                                    </span>
                                )}
                                {fontPreview && (
                                    <div className="text-6xl md:text-7xl font-light text-[#1A1A1A] tracking-tight">
                                        {fontPreview}
                                    </div>
                                )}
                                {fontDesc && (
                                    <p className="text-zinc-650 text-[11px] leading-relaxed font-light max-w-xl">
                                        {fontDesc}
                                    </p>
                                )}
                            </div>
                        </div>

                        {/* Color Swatches Grid */}
                        {swatches && swatches.length > 0 && (
                            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                                {swatches.map((swatch, idx) => (
                                    <div key={idx} className="bg-white border border-[#EDE6DD] rounded-xl p-6 flex flex-col justify-between h-48 shadow-sm">
                                        <div className="flex justify-between items-start">
                                            <span className="text-[10px] font-mono text-zinc-400">HEX / {swatch.hex}</span>
                                            <div 
                                                className="w-5 h-5 rounded-full border border-[#EDE6DD]" 
                                                style={{ backgroundColor: swatch.hex }}
                                            />
                                        </div>
                                        <div>
                                            <h3 className="text-sm font-semibold text-[#1A1A1A]">{swatch.name}</h3>
                                            <p className="text-zinc-500 text-[10px] leading-relaxed font-light mt-1.5">
                                                {swatch.description}
                                            </p>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        )}

                    </div>

                </div>
            </div>
        </section>
    );
}
